<script setup>
/* global axios */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import {
  buildCrmContextMessage,
  isCrmOpportunityNavigateMessage,
  isCrmNavigateMessage,
  isCurrentCrmConversation,
  isTrustedCrmMessage,
  normalizeCrmConversationSession,
} from './crm-conversation-protocol';
import { setPendingCrmOpportunity } from './crm-navigation-state';
import {
  buildCrmSessionMessage,
  crmConversationWorkspaceUrl,
  isTrustedCrmReadyMessage,
  normalizeAccountId,
  validateHubOrigin,
} from '../../routes/dashboard/crm/crm-host-protocol';

const props = defineProps({
  accountId: {
    type: [String, Number],
    required: true,
  },
  conversationId: {
    type: [String, Number],
    required: true,
  },
  routeAccountId: {
    type: [String, Number],
    default: null,
  },
  routeConversationId: {
    type: [String, Number],
    default: null,
  },
  agentId: {
    type: [String, Number],
    default: null,
  },
});

const router = useRouter();
const { t } = useI18n();
const crmFrame = ref(null);
const iframeSrc = ref('');
const iframeKey = ref(0);
const hubOrigin = ref('');
const status = ref('loading');
const errorMessage = ref('');
const frameReady = ref(false);
const sessionRequest = ref(null);
const sessionBlocked = ref(false);
const isMounted = ref(false);
const hubReady = ref(false);
let generation = 0;
let contextGeneration = 0;
let requestController = null;
let sessionController = null;
let handshakeTimer = null;

const normalizedAccountId = computed(() => normalizeAccountId(props.accountId));
const normalizedConversationId = computed(() =>
  normalizeAccountId(props.conversationId)
);
const normalizedRouteAccountId = computed(() =>
  normalizeAccountId(props.routeAccountId)
);
const normalizedRouteConversationId = computed(() =>
  normalizeAccountId(props.routeConversationId)
);
const contextMatchesRoute = computed(() =>
  isCurrentCrmConversation(
    {
      type: 'crm:context',
      accountId: normalizedAccountId.value,
      conversationId: normalizedConversationId.value,
    },
    normalizedRouteAccountId.value,
    normalizedRouteConversationId.value
  )
);
const contextMessage = computed(() => {
  // Agent identifiers are intentionally read only after the current Chatwoot
  // route and currentChat anchor agree. Chatwoot contact IDs are not CRM IDs.
  if (!contextMatchesRoute.value) return null;
  return buildCrmContextMessage(
    {
      accountId: normalizedAccountId.value,
      conversationId: normalizedConversationId.value,
      agentId: props.agentId,
    },
    normalizedAccountId.value
  );
});

const statusMessage = computed(() => {
  if (status.value === 'disabled') {
    return t(
      'CRM_CONVERSATION.DISABLED',
      'CRM is not enabled for this account.'
    );
  }
  if (status.value === 'error') {
    return (
      errorMessage.value ||
      t('CRM_CONVERSATION.ERROR', 'CRM context is temporarily unavailable.')
    );
  }
  if (status.value === 'ready') {
    return t(
      'CRM_CONVERSATION.READY',
      'CRM context for this conversation is ready.'
    );
  }
  return t('CRM_CONVERSATION.LOADING', 'Loading CRM context…');
});

function isCurrent(run, expectedAccountId) {
  return (
    isMounted.value &&
    generation === run &&
    normalizedAccountId.value === expectedAccountId &&
    contextMatchesRoute.value
  );
}

function isAbortError(error) {
  return error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED';
}

function httpStatus(error) {
  return error?.response?.status;
}

function clearFrameSession() {
  const frame = crmFrame.value;
  if (!frame?.contentWindow || !hubOrigin.value) return;
  frame.contentWindow.postMessage({ type: 'crm:clear' }, hubOrigin.value);
}

function clearHandshakeTimer() {
  if (handshakeTimer === null) return;
  clearTimeout(handshakeTimer);
  handshakeTimer = null;
}

function abortSessionRequest() {
  sessionController?.abort();
  sessionController = null;
}

function resetState() {
  clearFrameSession();
  clearHandshakeTimer();
  abortSessionRequest();
  hubOrigin.value = '';
  iframeSrc.value = '';
  frameReady.value = false;
  hubReady.value = false;
  sessionRequest.value = null;
  sessionBlocked.value = false;
  errorMessage.value = '';
  iframeKey.value += 1;
}

function stopForInvalidContext() {
  const sameAccountAnchor =
    normalizedAccountId.value &&
    normalizedAccountId.value === normalizedRouteAccountId.value;
  if (sameAccountAnchor && iframeSrc.value) {
    status.value = 'error';
    errorMessage.value = t(
      'CRM_CONVERSATION.INVALID_CONTEXT',
      'This conversation cannot open CRM context.'
    );
    return;
  }
  requestController?.abort();
  requestController = null;
  generation += 1;
  resetState();
  status.value = 'error';
  errorMessage.value = t(
    'CRM_CONVERSATION.INVALID_CONTEXT',
    'This conversation cannot open CRM context.'
  );
}

function scheduleHandshakeTimeout(run, expectedAccountId) {
  clearHandshakeTimer();
  handshakeTimer = setTimeout(() => {
    handshakeTimer = null;
    if (!isCurrent(run, expectedAccountId) || frameReady.value) return;
    sessionBlocked.value = true;
    status.value = 'error';
    errorMessage.value = t(
      'CRM_CONVERSATION.TIMEOUT',
      'CRM context did not respond. Try again.'
    );
  }, 15_000);
}

async function loadAccount() {
  generation += 1;
  const run = generation;
  const expectedAccountId = normalizedAccountId.value;
  requestController?.abort();
  resetState();
  status.value = 'loading';

  if (
    !expectedAccountId ||
    !contextMatchesRoute.value ||
    !contextMessage.value
  ) {
    stopForInvalidContext();
    return;
  }

  const controller = new AbortController();
  requestController = controller;
  try {
    const response = await axios.get(
      '/api/v1/accounts/' + expectedAccountId + '/crm/config',
      {
        signal: controller.signal,
        timeout: 15_000,
      }
    );
    if (!isCurrent(run, expectedAccountId)) return;

    const origin = validateHubOrigin(response?.data?.hubOrigin);
    const url = crmConversationWorkspaceUrl(origin);
    if (!origin || !url) throw new Error('invalid CRM origin');
    hubOrigin.value = origin;
    iframeSrc.value = url;
    scheduleHandshakeTimeout(run, expectedAccountId);
  } catch (error) {
    if (!isCurrent(run, expectedAccountId) || isAbortError(error)) return;
    status.value = httpStatus(error) === 404 ? 'disabled' : 'error';
    errorMessage.value =
      status.value === 'disabled'
        ? t('CRM_CONVERSATION.DISABLED', 'CRM is not enabled for this account.')
        : t(
            'CRM_CONVERSATION.ERROR',
            'CRM context is temporarily unavailable.'
          );
  } finally {
    if (requestController === controller) requestController = null;
  }
}

function sendContext(expectedGeneration = contextGeneration) {
  const frame = crmFrame.value;
  const context = contextMessage.value;
  if (
    !isMounted.value ||
    !contextMatchesRoute.value ||
    expectedGeneration !== contextGeneration ||
    !frameReady.value ||
    !frame?.contentWindow ||
    !hubOrigin.value ||
    !context
  ) {
    return false;
  }
  frame.contentWindow.postMessage(context, hubOrigin.value);
  return true;
}

function sendSession(session, expectedAccountId, run) {
  const frame = crmFrame.value;
  const message = buildCrmSessionMessage(
    session,
    expectedAccountId,
    hubOrigin.value
  );
  if (!frame?.contentWindow || !message || !isCurrent(run, expectedAccountId)) {
    return false;
  }
  frame.contentWindow.postMessage(message, hubOrigin.value);
  frameReady.value = true;
  sendContext();
  clearHandshakeTimer();
  status.value = 'ready';
  return true;
}

function requestSession() {
  const expectedAccountId = normalizedAccountId.value;
  const expectedOrigin = hubOrigin.value;
  const run = generation;
  if (
    !expectedAccountId ||
    !expectedOrigin ||
    !contextMatchesRoute.value ||
    sessionBlocked.value ||
    sessionRequest.value
  ) {
    return;
  }

  const controller = new AbortController();
  sessionController = controller;
  const request = axios
    .post('/api/v1/accounts/' + expectedAccountId + '/crm/session', undefined, {
      signal: controller.signal,
      timeout: 15_000,
    })
    .then(response => {
      if (!isCurrent(run, expectedAccountId)) return;
      const session = normalizeCrmConversationSession(
        response?.data,
        expectedAccountId,
        expectedOrigin
      );
      if (!session) throw new Error('invalid CRM session');
      sendSession(session, expectedAccountId, run);
    })
    .catch(error => {
      if (!isCurrent(run, expectedAccountId) || isAbortError(error)) return;
      sessionBlocked.value = true;
      status.value = 'error';
      errorMessage.value = t(
        'CRM_CONVERSATION.ERROR',
        'CRM context is temporarily unavailable.'
      );
    })
    .finally(() => {
      if (sessionRequest.value === request) sessionRequest.value = null;
      if (sessionController === controller) sessionController = null;
    });
  sessionRequest.value = request;
}

function onMessage(event) {
  const frame = crmFrame.value;
  if (!isMounted.value || !frame?.contentWindow || !hubOrigin.value) {
    return;
  }
  if (isTrustedCrmReadyMessage(event, hubOrigin.value, frame.contentWindow)) {
    hubReady.value = true;
    if (!contextMatchesRoute.value) return;
    requestSession();
    return;
  }
  if (!contextMatchesRoute.value) return;
  if (
    !isTrustedCrmMessage(
      event,
      hubOrigin.value,
      frame.contentWindow,
      normalizedAccountId.value
    )
  ) {
    return;
  }
  if (isCrmOpportunityNavigateMessage(event.data, normalizedAccountId.value)) {
    const opportunityId = event.data.opportunityId;
    if (setPendingCrmOpportunity(normalizedAccountId.value, opportunityId)) {
      router.push({
        name: 'crm_workspace',
        params: { accountId: normalizedAccountId.value },
      });
    }
    return;
  }
  if (!isCrmNavigateMessage(event.data)) return;
  const conversationId = normalizeAccountId(event.data.conversationId);
  if (!conversationId || !normalizedAccountId.value) return;
  router.push({
    name: 'inbox_conversation',
    params: {
      accountId: normalizedAccountId.value,
      conversation_id: conversationId,
    },
  });
}

function retry() {
  if (contextMatchesRoute.value) loadAccount();
}

function openWorkspace() {
  if (!normalizedAccountId.value || !contextMatchesRoute.value) return;
  router.push({
    name: 'crm_workspace',
    params: { accountId: normalizedAccountId.value },
  });
}

watch([normalizedAccountId, normalizedRouteAccountId], () => {
  if (isMounted.value) loadAccount();
});

watch(
  [
    normalizedConversationId,
    normalizedRouteConversationId,
    contextMatchesRoute,
    contextMessage,
  ],
  ([, , matches]) => {
    contextGeneration += 1;
    if (!isMounted.value) return;
    if (!matches) {
      stopForInvalidContext();
      return;
    }
    if (!hubOrigin.value && !iframeSrc.value && !requestController) {
      loadAccount();
      return;
    }
    if (hubReady.value && !frameReady.value && !sessionRequest.value) {
      requestSession();
      return;
    }
    if (sendContext()) {
      status.value = 'ready';
      errorMessage.value = '';
    }
  }
);

onMounted(() => {
  isMounted.value = true;
  window.addEventListener('message', onMessage);
  loadAccount();
});

onBeforeUnmount(() => {
  isMounted.value = false;
  generation += 1;
  contextGeneration += 1;
  requestController?.abort();
  requestController = null;
  abortSessionRequest();
  clearHandshakeTimer();
  clearFrameSession();
  window.removeEventListener('message', onMessage);
});
</script>

<template>
  <aside
    class="crm-conversation-panel flex h-80 shrink-0 flex-col min-h-0 border-t border-n-weak bg-n-background"
    data-testid="crm-conversation-panel"
    aria-label="CRM context"
  >
    <header class="flex items-center justify-between gap-2 px-3 py-2">
      <h2 class="text-sm font-semibold text-n-slate-12">
        {{ t('CRM_CONVERSATION.TITLE', 'CRM') }}
      </h2>
      <button
        type="button"
        class="px-2 py-1 rounded-lg outline outline-1 outline-n-weak text-xs text-n-slate-12 hover:bg-n-alpha-2"
        @click="openWorkspace"
      >
        {{ t('CRM_CONVERSATION.OPEN', 'Open Deals') }}
      </button>
    </header>

    <p
      v-if="!frameReady || status !== 'ready'"
      class="px-3 pb-2 text-xs text-n-slate-11"
      :class="{ 'text-n-ruby-11': status === 'error' || status === 'disabled' }"
      :role="status === 'error' || status === 'disabled' ? 'alert' : 'status'"
      aria-live="polite"
    >
      {{ statusMessage }}
      <button
        v-if="status === 'error'"
        type="button"
        class="ml-2 underline"
        @click="retry"
      >
        {{ t('CRM_CONVERSATION.RETRY', 'Retry') }}
      </button>
    </p>

    <iframe
      v-if="iframeSrc"
      ref="crmFrame"
      :key="iframeKey"
      :src="iframeSrc"
      :title="t('CRM_CONVERSATION.TITLE', 'CRM')"
      v-show="contextMatchesRoute"
      class="w-full flex-1 min-h-0 border-0"
    />
  </aside>
</template>
